<div fxLayout="column" fxLayoutGap="15px" *ngIf="selectedSpace">

  @if (dirty) {
    <app-info-panel class="pt-10"
    [message]="['There are unsaved changes']"
    [fullWidth]="true"
    [type]="PanelType.WARN"
    [buttons]="true"
    [buttonLabels]="['Cancel', 'Update']"
    [buttonDisabled]="[false, !isFormValid]"
    (emitEvent)="$event===1 ? saveSelectedSpace() : cancelChanges()">
  </app-info-panel>
  }

  <!-- Quick Statistics Section (always visible) -->
  <div class="info-card-dark" fxLayout="column" fxLayoutGap="10px">
    <span class="heading-regular">Quick Statistics</span>
    <div fxLayout="row" fxLayoutAlign="center space-between" fxLayoutGap="10px">
      <div class="quick-stat-item">
        <span class="quick-stat-title" style="font-size: 14px;">Size</span>
        <span class="quick-stat-content">{{getSelectedSpaceSize()}}m<sup>2</sup></span>
      </div>
      <div class="quick-stat-item" matTooltip="Percentage of days this space has been occupied since its start date">
        <span class="quick-stat-title" style="font-size: 14px;">Occ. Rate</span>
        <span class="quick-stat-content">{{getAverageOccupancy()}}%</span>
      </div>
      <div class="quick-stat-item">
        <span class="quick-stat-title" style="font-size: 14px;">Avg term</span>
        <span class="quick-stat-content">{{getSpaceAverageTerm()}}</span>
      </div>
    </div>
    <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="10px">
      <div class="quick-stat-item" style="width: 100%">
        <span class="quick-stat-title" style="font-size: 14px;">Avg Rental Income Per m<sup>2</sup></span>
        <span class="quick-stat-content">R{{getSpaceAveragePrice()}} m<sup>2</sup></span>
      </div>
    </div>
  </div>

  <!-- Space Configuration Section -->
  <div class="info-card-dark" fxLayout="column" fxLayoutGap="15px">
    <span class="heading-regular">Space Configuration</span>

    <div [formGroup]="spaceDetailsForm" fxLayout="column" fxLayoutGap="15px">
      <mat-form-field appearance="outline" class="custom-text-field w-100" floatLabel="always">
        <mat-label>Space Name</mat-label>
        <input (keydown.enter)="saveSelectedSpace()" formControlName="spaceName" matInput placeholder="Enter space name"/>
        <mat-icon class="material-icons-outlined search-icon-suffix" matIconSuffix>edit</mat-icon>
        <mat-error *ngIf="spaceDetailsForm.get('spaceName')!.hasError('required')" class="error-text">Space name is required</mat-error>
      </mat-form-field>

      <mat-form-field appearance="outline" class="custom-text-field w-100" floatLabel="always">
        <mat-label>Size</mat-label>
        <input formControlName="spaceSize" matInput type="number" placeholder="Space size" min="0"/>
        <div matSuffix class="me-2"><span class="ms-2">m<sup>2</sup></span></div>
        <mat-error *ngIf="spaceDetailsForm.get('spaceSize')!.hasError('required')" class="error-text">Size is required</mat-error>
      </mat-form-field>
    </div>
  </div>

  <!-- Availability Section -->
  <div class="info-card-dark" fxLayout="column" fxLayoutGap="15px">
    <span class="heading-regular">Availability</span>

    <div [formGroup]="spaceDetailsForm" fxLayout="column" fxLayoutGap="15px">
      <mat-form-field appearance="outline" class="custom-date-picker w-100" floatLabel="always">
        <mat-label>{{selectedSpace.locked ? 'Make available from' : 'Available from'}}</mat-label>
        <input [matDatepicker]="startDatepicker" formControlName="startDate" matInput>
        <mat-datepicker-toggle [for]="startDatepicker" matIconSuffix>
          <mat-icon matDatepickerToggleIcon>keyboard_arrow_down</mat-icon>
        </mat-datepicker-toggle>
        <mat-datepicker #startDatepicker></mat-datepicker>
        <mat-error *ngIf="spaceDetailsForm.get('startDate')!.hasError('required')" class="error-text">Start date is required</mat-error>
      </mat-form-field>

      <mat-form-field *ngIf="selectedSpace.locked === false" appearance="outline" class="custom-date-picker w-100" floatLabel="always">
        <mat-label>Make unavailable from</mat-label>
        <input [matDatepicker]="endDatepicker"
               [min]="selectedSpace.earliestEndDate ? dateUtils.displayShortDate(selectedSpace!.earliestEndDate) : getDate"
               formControlName="endDate" matInput>
        <mat-datepicker-toggle [for]="endDatepicker" matIconSuffix>
          <mat-icon matDatepickerToggleIcon>keyboard_arrow_down</mat-icon>
        </mat-datepicker-toggle>
        <mat-datepicker #endDatepicker></mat-datepicker>
        <mat-error *ngIf="spaceDetailsForm.get('endDate')!.hasError('required')" class="error-text">End date is required</mat-error>
        <mat-error *ngIf="spaceDetailsForm.get('endDate')!.hasError('endDateBeforeStartDate')">End date must be after start date</mat-error>
      </mat-form-field>

      <!-- Delete Space Option -->
      <div *ngIf="selectedSpace.locked === true && !selectedSpace?.hasHadLease" fxLayout="row"
           fxLayoutAlign="space-between center" class="mt-2">
        <span class="body-1-default" style="color: #f44336;">Delete Space</span>
        <button (click)="deleteSelectedSpace()" class="primary-color" mat-icon-button>
          <span class="material-icons-outlined">delete</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Preferred Industries Section -->
  <div class="info-card-dark" fxLayout="column" fxLayoutGap="15px">
    <div fxLayout="column" fxLayoutGap="5px">
      <span class="heading-regular">Preferred Industries</span>
      <span class="body-2-regular" style="color: #888; font-size: 12px;">
        {{selectedSpace.locked === false ? 'Add up to 3 preferred industries for this space' : 'Previously selected preferred industries'}}
      </span>
    </div>

    <div [formGroup]="preferredIndustriesForm" fxLayout="column" fxLayoutGap="10px">
      <div formArrayName="industries" fxLayout="column" fxLayoutGap="10px">

        <div *ngFor="let ctrl of industriesArray.controls; let i = index"
             fxLayout="row"
             fxLayoutAlign="start center"
             fxLayoutGap="10px">
          <mat-form-field appearance="outline" class="custom-select-field" style="flex: 1;">
            <mat-select class="custom-select-field-value" placeholder="Select Industries"
                        [formControlName]="i" [required]="true" [disabled]="selectedSpace.locked">
              @for (cat of allIndustries; track cat.id) {
                <mat-option class="custom-select-field-option" [value]="cat.code">{{cat.name}}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <button (click)="clearIndustrySelection(i)"
                  [disabled]="selectedSpace.locked"
                  class="warn-orange-color"
                  color="warn"
                  mat-icon-button>
            <span class="material-icons-outlined">delete</span>
          </button>
        </div>

        <!-- NEW: Add Industry Button (only if less than MAX_PREFERRED_INDUSTRIES and space is unlocked) -->
        <div *ngIf="industriesArray.length < MAX_PREFERRED_INDUSTRIES && !selectedSpace.locked"
             fxLayout="row"
             fxLayoutAlign="start center">
          <button (click)="addIndustrySlot()" class="button-secondary" mat-flat-button>
            <mat-icon>add</mat-icon>
            <span class="button-text">Add Industry</span>
          </button>
        </div>
      </div>
    </div>
  </div>

</div>
